/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/CopyScheduleModal.tsx
 * PURPOSE: Modal [17b] "Sao chép lịch" — khoảng NGUỒN/ĐÍCH tuỳ ý (không ép 7
 *          ngày), thay thế cách nút "Sao chép lịch" [17] hoạt động: khoảng
 *          ĐÍCH = khoảng đang lọc trên toolbar (CHỈ ĐỌC ở đây — "một nơi
 *          quyết định phạm vi"); khoảng NGUỒN chọn lại được ngay trong modal
 *          qua ContinuousScrollDatePicker. Bắt buộc xem trước (dry_run, KHÔNG
 *          ghi DB) trước khi ghi thật; nút "Sao chép" gọi endpoint commit —
 *          server TÍNH LẠI TỪ ĐẦU (vá L-03 của prototype), FE không tự gửi
 *          lại plan đã render.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Sao chép lịch hàng loạt (nguồn N ngày → đích M ngày,
 *   lặp theo chu kỳ nguồn khi N≠M, cắt cụt ở cuối)
 * - Impact: Operations — giảm nhập tay lặp lại cho tuần/khoảng giống nhau
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (modal, preview table + skip list)
 * - Pattern: `useCopyRangePreview` (useQuery, tự fetch lại mỗi khi đổi khoảng
 *   nguồn — derived data) → `useCopyRangeCommit` (useMutation, ghi thật)
 * - Dependencies: useManagerSchedule (useCopyRangePreview/useCopyRangeCommit),
 *   ContinuousScrollDatePicker (chọn lại khoảng nguồn), scheduleShared
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/sc009-build-prototype.html
 *   `openCopyModal(src)`/`computeCopyPlan(src)` (nguồn chính — xem đối chiếu
 *   trong PR description; wireframe [17]/[17b] chỉ là khung, prototype là
 *   nguồn thiết kế đích chi tiết theo chỉ đạo lượt này).
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py
 *   (`copy_range_preview`/`copy_range_commit`/`_compute_copy_range_plan`)
 * - Hooks: frontend/src/hooks/useManagerSchedule.ts
 * - Caller: frontend/src/pages/manager/ManagerSchedulePage.tsx (toolbar
 *   [Sao chép lịch] → menu 3 lựa chọn → modal này)
 * - Sibling pattern: frontend/src/pages/manager/schedule/QuickEntryModal.tsx
 * ===============================================================================
 */

import React, { useMemo, useState } from "react";
import { useCopyRangeCommit, useCopyRangePreview } from "../../../hooks/useManagerSchedule";
import {
  IMPORT_SKIP_REASON_LABELS_VI,
  type CopyRangeRequestPayload,
  type CopyWeekResult,
} from "../../../services/api/managerApi";
import { ContinuousScrollDatePicker, type DateFilterValue } from "../../../components/common/ContinuousScrollDatePicker";
import { extractApiError, formatDateVi, formatDayMonth, formatWeekRange } from "./scheduleShared";
import styles from "../ManagerSchedulePage.module.css";

/** Cùng trần khoảng NGUỒN với [1c]/[1d] toolbar (wireframe §SC-009 [CAL] "210 ngày") */
const SOURCE_MAX_RANGE_DAYS = 210;

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={16} height={16} aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="16" rx="2" />
    <path strokeLinecap="round" d="M8 2.5v4M16 2.5v4M3 9.5h18" />
  </svg>
);
const WarnIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
  </svg>
);

/** ngày YYYY-MM-DD + n ngày → YYYY-MM-DD (local) — cùng `addDaysISO` scheduleShared,
 * chỉ đếm số ngày inclusive giữa 2 mốc (không cần import riêng cho 1 phép trừ). */
const daysBetweenInclusive = (fromISO: string, toISO: string): number =>
  Math.round(
    (new Date(`${toISO}T00:00:00`).getTime() - new Date(`${fromISO}T00:00:00`).getTime()) / 86_400_000,
  ) + 1;

/** Gộp danh sách skip THÔ (itemized theo ngày/NV/lý do — backend giữ nguyên
 * để có thể truy lại từng dòng, xem docstring `CopyRangeSkipItem`) thành đếm
 * theo LÝ DO cho hiển thị "gộp theo lý do dễ đọc" — cùng cách `formatCopyWeekResultToast`
 * (ManagerSchedulePage.tsx) đã xử lý `skipped_reasons` của endpoint cũ. */
function countSkipsByReason(skipped: { reason: string }[]): Record<string, number> {
  const out: Record<string, number> = {};
  skipped.forEach((s) => {
    out[s.reason] = (out[s.reason] ?? 0) + 1;
  });
  return out;
}

export interface CopyScheduleModalProps {
  /** Khoảng NGUỒN mặc định lúc mở (theo lựa chọn menu tuần trước/tháng trước/khoảng ngày) — sửa lại được ngay trong modal */
  initialSourceFrom: string;
  initialSourceTo: string;
  /** Khoảng ĐÍCH — khoảng đang lọc trên toolbar. CHỈ ĐỌC ở đây (đổi bằng lịch trên thanh phạm vi, "một nơi quyết định phạm vi"). */
  targetFrom: string;
  targetTo: string;
  onClose: () => void;
  onToast: (message: string) => void;
}

const CopyScheduleModal: React.FC<CopyScheduleModalProps> = ({
  initialSourceFrom,
  initialSourceTo,
  targetFrom,
  targetTo,
  onClose,
  onToast,
}) => {
  const [sourceFrom, setSourceFrom] = useState(initialSourceFrom);
  const [sourceTo, setSourceTo] = useState(initialSourceTo);
  const [commitError, setCommitError] = useState<string | null>(null);

  const targetDays = daysBetweenInclusive(targetFrom, targetTo);

  // ContinuousScrollDatePicker (pickDay/confirmRange) tự đảm bảo from<=to lúc
  // áp dụng — nhánh else đây THUẦN PHÒNG THỦ (cùng tinh thần `rangeInvalid` ở
  // ManagerSchedulePage.tsx), không có đường nào tới được trong luồng bình
  // thường.
  const sourceInvalid = sourceTo < sourceFrom;

  const previewParams: CopyRangeRequestPayload | null = sourceInvalid
    ? null
    : { source_from: sourceFrom, source_to: sourceTo, target_from: targetFrom, target_to: targetTo };
  const previewQuery = useCopyRangePreview(previewParams);
  const commitMutation = useCopyRangeCommit();

  const applySourceSelection = (value: DateFilterValue) => {
    if (value.mode === "single") {
      setSourceFrom(value.date);
      setSourceTo(value.date);
    } else {
      setSourceFrom(value.from);
      setSourceTo(value.to);
    }
  };

  const planCount = previewQuery.data?.items.length ?? 0;
  const skipReasonCounts = useMemo(
    () => (previewQuery.data ? countSkipsByReason(previewQuery.data.skipped) : {}),
    [previewQuery.data],
  );
  const lengthMismatch =
    !!previewQuery.data && previewQuery.data.source_days !== previewQuery.data.target_days;

  const handleCommit = () => {
    if (commitMutation.isPending || sourceInvalid || planCount === 0) return;
    setCommitError(null);
    const payload: CopyRangeRequestPayload = {
      source_from: sourceFrom,
      source_to: sourceTo,
      target_from: targetFrom,
      target_to: targetTo,
    };
    // Gọi ĐÚNG endpoint commit với ĐÚNG body preview đang hiện — server tự
    // TÍNH LẠI TỪ ĐẦU (vá L-03), FE không tự "recompute" hay gửi lại `items`
    // đã render (xem docstring copy_range_commit backend).
    commitMutation.mutate(payload, {
      onSuccess: (result: CopyWeekResult) => {
        onToast(formatCopyRangeResultToast(result));
        onClose();
      },
      onError: (error) => {
        setCommitError(extractApiError(error).message);
      },
    });
  };

  const footLabel = previewQuery.isLoading
    ? "Đang tính…"
    : planCount > 0
      ? `Sao chép ${planCount} ca`
      : "Không còn ngày nào hợp lệ để sao chép";
  const commitDisabled =
    sourceInvalid || planCount === 0 || previewQuery.isLoading || previewQuery.isError || commitMutation.isPending;

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" aria-labelledby="copyScheduleTitle">
        <div className={styles.modalHead}>
          <h2 className={styles.modalTitle} id="copyScheduleTitle">
            Sao chép lịch
          </h2>
          <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          {commitError && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
              {commitError}
            </div>
          )}

          {/* Khoảng đích — CHỈ ĐỌC (prototype openCopyModal: "Một nơi quyết định
              phạm vi — đổi khoảng đích bằng lịch trên thanh phạm vi."). */}
          <p style={{ margin: 0 }}>
            Khoảng đích (đang lọc): <strong>{formatWeekRange(targetFrom, targetTo)}</strong> · {targetDays} ngày
            <br />
            <span className={`${styles.muted} ${styles.txtSm}`}>
              Một nơi quyết định phạm vi — đổi khoảng đích bằng lịch trên thanh phạm vi.
            </span>
          </p>

          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-3)" }}>
            Khoảng nguồn
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--nqh-space-2)" }}>
            <CalendarIcon />
            <ContinuousScrollDatePicker
              value={sourceFrom === sourceTo ? { mode: "single", date: sourceFrom } : { mode: "range", from: sourceFrom, to: sourceTo }}
              onApply={applySourceSelection}
              maxRangeDays={SOURCE_MAX_RANGE_DAYS}
              triggerClassName={styles.selectBtn}
              triggerLabel={
                sourceInvalid
                  ? "Khoảng nguồn không hợp lệ"
                  : `${formatDayMonth(sourceFrom)} – ${formatDateVi(sourceTo)} · ${daysBetweenInclusive(sourceFrom, sourceTo)} ngày`
              }
              ariaLabel="Chọn khoảng nguồn sao chép"
            />
          </span>

          {sourceInvalid && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginTop: "var(--nqh-space-3)" }}>
              Khoảng nguồn không hợp lệ: ngày bắt đầu phải trước hoặc bằng ngày kết thúc.
            </div>
          )}

          {lengthMismatch && previewQuery.data && (
            <div className={`${styles.banner} ${styles.bannerWarning}`} role="status" style={{ marginTop: "var(--nqh-space-3)" }}>
              <span className="ico" aria-hidden="true">
                <WarnIcon />
              </span>
              <span>
                Hai khoảng khác độ dài — lặp theo chu kỳ nguồn ({previewQuery.data.source_days} ngày), cắt cụt ở cuối.
              </span>
            </div>
          )}

          {previewQuery.isError && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginTop: "var(--nqh-space-3)" }}>
              {extractApiError(previewQuery.error).message}
            </div>
          )}

          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-4)" }}>
            Xem trước bản sao chép
          </div>
          {previewQuery.isLoading && <p className={styles.muted}>Đang tính bản xem trước…</p>}
          {!previewQuery.isLoading && !sourceInvalid && previewQuery.data && (
            <>
              {planCount > 0 ? (
                <table className={styles.copyPreviewTable}>
                  <thead>
                    <tr>
                      <th>Ngày đích</th>
                      <th>Nhân viên</th>
                      <th>Ca</th>
                      <th>Nguồn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewQuery.data.items.map((item, idx) => (
                      <tr key={`${item.target_date}-${item.user_id}-${idx}`}>
                        <td>{formatDateVi(item.target_date)}</td>
                        <td>{item.full_name}</td>
                        <td>{item.shift_label}</td>
                        <td className={styles.muted}>nguồn: {formatDayMonth(item.source_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className={styles.muted}>Không có ca nào để chép.</p>
              )}

              {previewQuery.data.skipped.length > 0 && (
                <ul className={styles.importErrors} style={{ marginTop: "var(--nqh-space-3)" }}>
                  {Object.entries(skipReasonCounts).map(([reason, count]) => (
                    <li key={reason}>
                      Bỏ qua {count} {IMPORT_SKIP_REASON_LABELS_VI[reason] ?? reason}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <div className={styles.modalFoot}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose}>
            Huỷ
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={commitDisabled} onClick={handleCommit}>
            {commitMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
            {footLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Toast [17b] "Sao chép {N} ca" — cùng khuôn `CopyWeekResult` với endpoint
 * cũ, cùng cách trình bày `formatCopyWeekResultToast` (ManagerSchedulePage.tsx)
 * nhưng câu không-bỏ-qua đổi từ "từ tuần trước" (luôn đúng khi nguồn=1 tuần cố
 * định) sang trung lập "Sao chép {N} ca" (nguồn ở đây có thể là tuần/tháng/
 * khoảng ngày tuỳ ý). Giữ RIÊNG trong file này (không refactor sang
 * scheduleShared.ts dùng chung với helper kia) — tránh đổi hành vi/toast của
 * nút "Sao chép lịch" cũ ngoài phạm vi việc này, dù có trùng vài dòng logic. */
function formatCopyRangeResultToast(result: CopyWeekResult): string {
  if (result.skipped <= 0) {
    return `Đã sao chép ${result.copied} ca`;
  }
  const reasonParts = Object.entries(result.skipped_reasons)
    .map(([reason, count]) => `${count} ${IMPORT_SKIP_REASON_LABELS_VI[reason] ?? reason}`)
    .join(", ");
  return `Đã sao chép ${result.copied} ca — bỏ qua ${result.skipped} ca (${reasonParts})`;
}

export default CopyScheduleModal;
